import {useCallback, useEffect, useState} from 'react';

import {useLocation, useNavigate} from 'react-router-dom';

export interface LocalQueryParams extends Record<string, string | undefined> {
  [key: string]: string | undefined;
}
export const useQueryParameterState = <T extends LocalQueryParams>(initParams?: T) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [localQueryParams, setLocalQueryParams] = useState<Partial<T>>({});
  const [startingParams, setStartingParams] = useState<Partial<T> | undefined>();

  const updateQueryParameters = useCallback(
    (params: Partial<T>, replace?: boolean) => {
      const query = new URLSearchParams(location.search);

      query.forEach((value, key) => {
        if (!Object.keys(params).find((entry) => entry === key)) {
          query.delete(key);
        }
      });

      Object.keys(params).forEach((key: string) => {
        if (params[key]) {
          query.set(key, params[key]);
        } else {
          query.delete(key);
        }
      });
      setLocalQueryParams(params);
      navigate(`${location.pathname}?${query.toString()}`, {replace});
    },
    [navigate, location.pathname, location.search]
  );

  useEffect(() => {
    if (initParams) {
      setLocalQueryParams(initParams);
    }
  }, [initParams]);

  const setStateFromQueryParameters = useCallback((search: string, initialLoad?: true) => {
    const localParams: LocalQueryParams = {};
    const query = new URLSearchParams(search);
    query.forEach((value, name) => {
      localParams[name] = value;
    });

    setLocalQueryParams(localParams as T);
    if (initialLoad) {
      setStartingParams(localParams as T);
    }
  }, []);

  // update state on query parameter change
  useEffect(() => {
    if (location.search) {
      setStateFromQueryParameters(location.search);
    }
  }, [location.search, setStateFromQueryParameters]);

  // on load
  useEffect(() => {
    // If parameters present in url, use them over "initParams" coming in from hook
    if (location.search) {
      setStateFromQueryParameters(location.search, true);
    } else {
      updateQueryParameters(initParams || {}, true);
    }
    //eslint-disable-next-line
  }, []);

  return {updateQueryParameters, params: localQueryParams, startingParams};
};
